import { PanelsTopLeft } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Field, NativeSelect } from "@ncp/ui";
import { CatalogPage } from "../features/generic/CatalogPage";
import type { DefinitionSummary } from "../features/generic/api";
import { SCREEN_KINDS, newScreenBody, useScreenOptions, type ScreenBody } from "../features/screens/api";

/** Screens: pages described as data (lists, details, forms, dashboards) that the back office and the portal show at /s/<key>. */
export function ScreensPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const options = useScreenOptions();
  const label = (r: { name: string; nameAr?: string | null }) => (locale === "ar" && r.nameAr ? r.nameAr : r.name);
  return (
    <CatalogPage<DefinitionSummary, ScreenBody>
      resource="screens"
      basePath="/screens"
      icon={PanelsTopLeft}
      title={t("admin:screens.title")}
      subtitle={t("admin:screens.subtitle")}
      navLabel={t("admin:nav.screens")}
      writePermission="platform.screens.write"
      newLabel={t("admin:screens.new")}
      emptyTitle={t("admin:screens.emptyTitle")}
      emptyBody={t("admin:screens.emptyBody")}
      docsAnchor="screens"
      help={<><p>{t("admin:screens.help.what")}</p><p>{t("admin:screens.help.portal")}</p><p>{t("admin:screens.help.release")}</p></>}
      newBody={newScreenBody}
      createFields={(extra, set) => {
        const surface = extra.surface ?? "backoffice";
        return (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:screens.fields.surface")}>
              <NativeSelect data-testid="create-surface" value={surface} onChange={(e) => set("surface", e.target.value)}>
                <option value="backoffice">{t("admin:screens.surfaces.backoffice")}</option>
                <option value="portal">{t("admin:screens.surfaces.portal")}</option>
              </NativeSelect>
            </Field>
            {surface === "backoffice" ? (
              <Field label={t("admin:screens.fields.kind")}>
                <NativeSelect data-testid="create-kind" value={extra.kind ?? "list"} onChange={(e) => set("kind", e.target.value)}>
                  {SCREEN_KINDS.map((k) => <option key={k} value={k}>{t(`admin:screens.kinds.${k}`)}</option>)}
                </NativeSelect>
              </Field>
            ) : null}
            {surface === "backoffice" ? (
              <Field label={t("admin:screens.fields.recordType")}>
                <NativeSelect data-testid="create-record-type" value={extra.recordType ?? ""} onChange={(e) => set("recordType", e.target.value)}>
                  <option value="">—</option>
                  {options.data?.recordTypes.map((r) => <option key={r.key} value={r.key}>{label(r)}</option>)}
                </NativeSelect>
              </Field>
            ) : null}
            <Field label={t("admin:screens.fields.service")}>
              <NativeSelect data-testid="create-service" value={extra.service ?? ""} onChange={(e) => set("service", e.target.value)}>
                <option value="">—</option>
                {options.data?.services.filter((s) => surface !== "portal" || s.portal).map((s) => <option key={s.key} value={s.key}>{label(s)}</option>)}
              </NativeSelect>
            </Field>
          </div>
        );
      }}
    />
  );
}
